import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FiTrendingUp, FiClock, FiAlertCircle, FiBarChart2, FiList, FiGrid, FiChevronRight } from 'react-icons/fi';
import MetaClaimsAI from '../components/common/MetaClaimsAI';
import { Plus } from 'lucide-react';
import {
  fetchWorkbenchMetrics,
  fetchCriticalTasksLane,
  fetchNeedAttentionLane,
  fetchPendingTasksLane,
  extractTaskDisplayData,
  DEFAULT_SLA_HOURS
} from '../services/manualTaskService';
import { getUserData } from '../utils/authUtils';
import {
  formatTimeRemaining,
  getTaskSeverity,
} from '../utils/taskStatistics';
import { Button } from '../styles/PageStyles';

const Container = styled.div`
  display: flex;
  padding: 1.5rem 0.5rem 1.5rem 1.5rem;
  background: #f3f5f7;
  min-height: 100vh;
  position: relative;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    padding: 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const MainContent = styled.div`
  container-type: inline-size;
  container-name: main-content;
  flex: 1;
  min-width: 400px;
  overflow: auto;
  padding-right: 0.5rem;
  overflow-x: hidden;
  
  @media (max-width: 1024px) {
    min-width: unset;
    padding-right: 0;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 768px) {
    min-width: unset;
  }
`;

const ResizeHandle = styled.div`
  width: 8px;
  cursor: col-resize;
  background: transparent;
  position: relative;
  flex-shrink: 0;
  transition: background 0.2s;
  
  &:hover {
    background: #e5e7eb;
  }
  
  &:active {
    background: #3b82f6;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 2px;
    height: 40px;
    background: #d1d5db;
    border-radius: 2px;
  }
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const Sidebar = styled.div`
  width: ${props => props.isOpen ? `${props.width}px` : '0px'};
  min-width: ${props => props.isOpen ? '300px' : '0px'};
  max-width: ${props => props.isOpen ? '600px' : '0px'};
  flex-shrink: 0;
  padding-left: ${props => props.isOpen ? '0.5rem' : '0'};
  overflow: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: width 0.3s ease-in-out, min-width 0.3s ease-in-out, padding-left 0.3s ease-in-out;
  
  @media (max-width: 1024px) {
    width: 100% !important;
    min-width: unset;
    max-width: unset;
    padding-left: 0;
  }
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.1);
  
  @container main-content (min-width: 1366px) {
    flex-direction: row;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const WelcomeCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  
  @container main-content (min-width: 1366px) {
    flex-direction: row;
    gap: 24px;
  }
  
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const SLACircle = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: conic-gradient(#38BDF8 ${props => props.percentage}%, #E0F2FE ${props => props.percentage}%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const SLAInner = styled.div`
  width: 105px;
  height: 105px;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 68px;
    height: 68px;
  }
`;

const SLALabelHeading = styled.div`
  font-size: 13px;
  color: #6b7280;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
const SLALabel = styled.div`
  font-size: 10px;
  color: #9ca3af;
`;

const SLAValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
  margin: 2px 0;
`;

const WelcomeContent = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
  justify-content: space-between;
  padding-left: 10px;
  overflow: hidden;
  
  @container main-content (min-width: 1366px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const WelcomeTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const WelcomeTextSecion = styled.div`

`

const WelcomeSubtitle = styled.p`
  font-size: 14px;
  color: #9ca3af;
  margin: 4px 0 0 0;
`;

const WelcomeTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AlertBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(232, 169, 0, 0.1);
  color: #404040;
  padding: 4px 16px 4px 0px;
  margin-top: 1rem;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  width: fit-content;
`;
const AlertCount = styled.span`
  font-size: 16px;
  color:#fff;
  background-color: #e8a900;
  height:30px;
  width:30px;
  border-radius:50%;
  display:flex;
  align-items:center;
  justify-content:center;
`;

const AnimatedChevron = styled(FiChevronRight)`
  color: #e8a900;
  font-size: 1.3rem;
  transition: transform 0.3s ease;
  
  ${AlertBadge}:hover & {
    transform: translateX(4px);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  padding: 24px;
  border-top: 1px dashed #d1d1d1;
  position: relative;
  
  &::after {
    content: "";
    height: 20px;
    width: 20px;
    background-color: transparent;
    top: -10px;
    display: block;
    position: absolute;
    right: -10px;
    border-top: 14px solid #f3f5f7;
    border-radius: 50%;
    border-left: 14px solid #f3f5f7;
    transform: rotate(-45deg);
  }

  &::before {
    content: "";
    height: 20px;
    width: 20px;
    background-color: transparent;
    top: -10px;
    display: block;
    position: absolute;
    left: -10px;
    border-bottom: 14px solid #f3f5f7;
    border-radius: 50%;
    border-right: 14px solid #f3f5f7;
    transform: rotate(-45deg);
  }
  
  @container main-content (min-width: 1366px) {
    grid-template-columns: repeat(4, 1fr);
    padding: 24px;
    border-top: none;
    border-left: 1px dashed #d1d1d1;
  }
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns:  repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #ffffff;
  padding: 20px 25px;
  border-radius: 8px;
  // box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  background-color: #f5f7fb;
`;

const StatHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: space-between;
`;

const StatValue = styled.div`
  font-size: 33px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const StatIcon = styled.div`
  color: #9ca3af;
  width: 20px;
  height: 20px;
  margin-bottom: 4px;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 4rem 0px 0.8rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    margin: 1.5rem 0 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 500;
  color: #282828;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ViewControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const ViewButton = styled.button`
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const KanbanContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  position: sticky;
  top: 64px;
  background: #ffffff;
  z-index: 10;
  padding: 1rem 0;
  overflow: hidden;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const KanbanColumn = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  min-width: 0;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ColumnHeader = styled.div`
  margin-bottom: 1rem;
  margin-top: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid #e3e3e3;
`;

const ColumnTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #282828;
  margin-bottom: 1.5rem;
  padding-left: 0.5rem;
`;

const sonarPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(var(--dot-color), 0.5);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(var(--dot-color), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--dot-color), 0);
  }
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color};
  position: relative;
  --dot-color: ${props => {
    const color = props.color;
    // Convert hex to RGB
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `${r}, ${g}, ${b}`;
  }};
  animation: ${sonarPulse} 2s ease-out infinite;
`;

const ColumnDescription = styled.div`
  font-size: 0.9rem;
    color: rgb(107, 114, 128);
    line-height: 1.4;
    // margin-bottom: 1rem;
    background-color: rgb(141 141 141 / 5%);
    padding: 15px;
    border-radius: 5px;
    word-spacing: 7px;
    
`;

const SortControls = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f3f4f6;
`;

const SortButton = styled.button`
  background: ${props => props.active ? '#eff6ff' : '#f3f5f7'};
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  border: none;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: #eff6ff;
    color: #3b82f6;
  }
`;

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height:calc(100dvh - 240px);
  overflow-y: auto;
`;

const TaskCard = styled.div`
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  margin: 3px 5px;
  border-left: 4px solid ${props => props.borderColor || '#ff3342'};
  padding-bottom: 14px;
  position: relative;
  max-width: 100%;
  
  &:hover {
    border-left-color: ${props => props.borderColor || '#ff3342'};
    // box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
    background-color:#f3f5f7
  }

  &::after{
    content: "";
    position: absolute;
    bottom: 0;
    left: 5px;
    right: 5px;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.1);
  }

  &:last-child::after{
    display: none;
  }
  
  &:last-child{
    margin-bottom: 10px;
  }

  @media (max-width: 768px) {
    padding: 0.875rem;
  }
`;

const TaskCategory = styled.div`
  font-size: clamp(0.75rem, 0.3vw + 0.6rem, 0.85rem);
  color: #a4a4a4;
  margin-bottom: 0.25rem;
`;

const TaskDate = styled.div`
  font-size: clamp(0.7rem, 0.3vw + 0.5rem, 0.8rem);
  color: #9ca3af;
  text-align: right;
`;

const ClaimedAmount = styled.div`
  font-size: clamp(0.8rem, 0.3vw + 0.6rem, 0.9rem);
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const TaskHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
  width: 100%;
  min-width: 0;
`;

const TaskId = styled.div`
  font-size: clamp(0.85rem, 0.3vw + 0.65rem, 0.95rem);
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TaskAmount = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
`;

const TaskDescription = styled.div`
  font-size: clamp(0.7rem, 0.3vw + 0.5rem, 0.8rem);
  color: #a4a4a4;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.5rem;
  min-width: 0;
  overflow: hidden;
`;

const TaskDescriptionText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const CheckIcon = styled.span`
  color: #10b981;
`;

const SectionLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  margin: 1rem 0 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CreateClaimButton = styled.button`
  background: #00a1ff;
  color: #ffffff;
  border: none;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  width: fit-content;
  text-align: center;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FloatingActionButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  // background: linear-gradient(135deg, #00B4D8 0%, #A855F7 100%);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1000;
  padding: 0;
  & > img {
    width: 100%;
    height: 100%;
  }
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const WorkbenchListing = () => {
  const navigate = useNavigate();
  const [sidebarWidth, setSidebarWidth] = useState(380);
  const [isResizing, setIsResizing] = useState(false);
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(true);
  const [activeSort, setActiveSort] = useState({
    critical: null,
    attention: null,
    pending: null
  });
  
  // Configurable SLA timeout in hours (change this value to adjust SLA calculation)
  const [slaTimeoutHours, setSlaTimeoutHours] = useState(DEFAULT_SLA_HOURS); // Default: 72 hours
  
  const [tasks, setTasks] = useState({ critical: [], attention: [], pending: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalCases: 0,
    avgTAT: 0,
    nearSLA: 0,
    highRisk: 0,
    slaPercentage: 0,
    yourCases: 0,
    criticalCount: 0,
    needAttentionCount: 0,
    pendingCount: 0
  });

  // Pagination state for each column
  const [pagination, setPagination] = useState({
    critical: { page: 1, hasMore: true, loading: false },
    attention: { page: 1, hasMore: true, loading: false },
    pending: { page: 1, hasMore: true, loading: false }
  });

  const itemsPerPage = 10;
  const criticalObserver = useRef(null);
  const attentionObserver = useRef(null);
  const pendingObserver = useRef(null);

  // IntersectionObserver callbacks for each column
  const criticalLoaderRef = useCallback(node => {
    if (!node) return;
    if (criticalObserver.current) criticalObserver.current.disconnect();

    criticalObserver.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && pagination.critical.hasMore && !pagination.critical.loading && !loading) {
        setPagination(prev => ({
          ...prev,
          critical: { ...prev.critical, page: prev.critical.page + 1, loading: true }
        }));
      }
    }, { rootMargin: '100px', threshold: 0.1 });

    criticalObserver.current.observe(node);
  }, [pagination.critical.hasMore, pagination.critical.loading, loading]);

  const attentionLoaderRef = useCallback(node => {
    if (!node) return;
    if (attentionObserver.current) attentionObserver.current.disconnect();

    attentionObserver.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && pagination.attention.hasMore && !pagination.attention.loading && !loading) {
        setPagination(prev => ({
          ...prev,
          attention: { ...prev.attention, page: prev.attention.page + 1, loading: true }
        }));
      }
    }, { rootMargin: '100px', threshold: 0.1 });

    attentionObserver.current.observe(node);
  }, [pagination.attention.hasMore, pagination.attention.loading, loading]);

  const pendingLoaderRef = useCallback(node => {
    if (!node) return;
    if (pendingObserver.current) pendingObserver.current.disconnect();

    pendingObserver.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && pagination.pending.hasMore && !pagination.pending.loading && !loading) {
        setPagination(prev => ({
          ...prev,
          pending: { ...prev.pending, page: prev.pending.page + 1, loading: true }
        }));
      }
    }, { rootMargin: '100px', threshold: 0.1 });

    pendingObserver.current.observe(node);
  }, [pagination.pending.hasMore, pagination.pending.loading, loading]);

  const buildLaneFilter = (laneKey) => {
    const sort = activeSort[laneKey];

    if (laneKey === 'critical' || laneKey === 'attention') {
      if (sort === 'High Priority') {
        return { sla_due_only: false, priority: 'high' };
      }
    }

    if (laneKey === 'pending') {
      if (sort === 'High Value') {
        return { sla_due_only: false, priority: 'high' };
      }
    }

    return { sla_due_only: false, priority: null };
  };

  // Handle resize functionality
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const container = document.querySelector('[data-container="workbench"]');
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const newWidth = containerRect.right - e.clientX - 24; // 24px for padding
      
      if (newWidth >= 300 && newWidth <= 600) {
        setSidebarWidth(newWidth);
      }
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Fetch metrics on initial load
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const metrics = await fetchWorkbenchMetrics();
        setStats(prev => ({
          ...prev,
          totalCases: metrics.totalCases,
          avgTAT: metrics.overallTatHours,
          nearSLA: metrics.nearSLA,
          highRisk: metrics.highRisk,
          slaPercentage: metrics.slaPercent,
        }));
      } catch (err) {
        console.error('Error fetching metrics:', err);
      }
    };
    fetchMetrics();
  }, []);

  // Fetch critical tasks with pagination
  useEffect(() => {
    const fetchCriticalTasks = async () => {
      const isInitialLoad = pagination.critical.page === 1;
      if (isInitialLoad) {
        setLoading(true);
      }

      try {
        const criticalFilter = buildLaneFilter('critical');
        const criticalFilterWithPagination = {...criticalFilter, skip: (pagination.critical.page - 1) * itemsPerPage, limit: itemsPerPage}
        // const queryParams = `skip=${(pagination.critical.page - 1) * itemsPerPage}&limit=${itemsPerPage}`;
        const criticalLane = await fetchCriticalTasksLane(criticalFilterWithPagination);

        if (criticalLane.length === 0 || criticalLane.length < itemsPerPage) {
          setPagination(prev => ({
            ...prev,
            critical: { ...prev.critical, hasMore: false, loading: false }
          }));
        } else {
          setPagination(prev => ({
            ...prev,
            critical: { ...prev.critical, loading: false }
          }));
        }

        setTasks(prev => ({
          ...prev,
          critical: isInitialLoad ? criticalLane : [...prev.critical, ...criticalLane]
        }));
      } catch (err) {
        console.error('Error fetching critical tasks:', err);
        setPagination(prev => ({
          ...prev,
          critical: { ...prev.critical, hasMore: false, loading: false }
        }));
      } finally {
        if (isInitialLoad) {
          setLoading(false);
        }
      }
    };

    fetchCriticalTasks();
  }, [pagination.critical.page, activeSort.critical]);

  // Fetch attention tasks with pagination
  useEffect(() => {
    const fetchAttentionTasks = async () => {
      const isInitialLoad = pagination.attention.page === 1;

      try {
        const attentionFilter = buildLaneFilter('attention');
        const attentionFilterWithPagination = {...attentionFilter, skip: (pagination.attention.page - 1) * itemsPerPage, limit: itemsPerPage}
        // const queryParams = `skip=${(pagination.attention.page - 1) * itemsPerPage}&limit=${itemsPerPage}`;
        const attentionLane = await fetchNeedAttentionLane(attentionFilterWithPagination);

        if (attentionLane.length === 0 || attentionLane.length < itemsPerPage) {
          setPagination(prev => ({
            ...prev,
            attention: { ...prev.attention, hasMore: false, loading: false }
          }));
        } else {
          setPagination(prev => ({
            ...prev,
            attention: { ...prev.attention, loading: false }
          }));
        }

        setTasks(prev => ({
          ...prev,
          attention: isInitialLoad ? attentionLane : [...prev.attention, ...attentionLane]
        }));
      } catch (err) {
        console.error('Error fetching attention tasks:', err);
        setPagination(prev => ({
          ...prev,
          attention: { ...prev.attention, hasMore: false, loading: false }
        }));
      }
    };

    fetchAttentionTasks();
  }, [pagination.attention.page, activeSort.attention]);

  // Fetch pending tasks with pagination
  useEffect(() => {
    const fetchPendingTasks = async () => {
      const isInitialLoad = pagination.pending.page === 1;

      try {
        const pendingFilter = buildLaneFilter('pending');
        const pendingFilterWithPagination = {...pendingFilter, skip: (pagination.pending.page - 1) * itemsPerPage, limit: itemsPerPage}
        // const queryParams = `skip=${(pagination.pending.page - 1) * itemsPerPage}&limit=${itemsPerPage}`;
        const pendingLane = await fetchPendingTasksLane(pendingFilterWithPagination);

        if (pendingLane.length === 0 || pendingLane.length < itemsPerPage) {
          setPagination(prev => ({
            ...prev,
            pending: { ...prev.pending, hasMore: false, loading: false }
          }));
        } else {
          setPagination(prev => ({
            ...prev,
            pending: { ...prev.pending, loading: false }
          }));
        }

        setTasks(prev => ({
          ...prev,
          pending: isInitialLoad ? pendingLane : [...prev.pending, ...pendingLane]
        }));
      } catch (err) {
        console.error('Error fetching pending tasks:', err);
        setPagination(prev => ({
          ...prev,
          pending: { ...prev.pending, hasMore: false, loading: false }
        }));
      }
    };

    fetchPendingTasks();
  }, [pagination.pending.page, activeSort.pending]);

  const handleTaskClick = (task) => {
    navigate(`/workbench/${task.id}`, {
      state: {
        manualTaskId: task.id,
        // Prefer legacy workflow_code if present, otherwise fall back to pipeline_id
        workflow_code: task.workflow_code || task.pipeline_id || null,
        // Prefer legacy workflow_instance_id if present, otherwise fall back to pipeline_instance_id
        workflow_instance_id: task.workflow_instance_id || task.pipeline_instance_id || null,
        // Pass overall SLA percentage for use in detail screens
        slaPercentage: stats.slaPercentage,
      }
    });
  };

  const handleCreateClaim = () => {
    navigate(`/create-claim`)
  }

  const renderTask = (task) => {
    const displayData = extractTaskDisplayData(task);
    const timeRemainingText = formatTimeRemaining(task);
    const severity = getTaskSeverity(task);
    
    return (
      <TaskCard key={task.id} onClick={() => handleTaskClick(task)}>
        <TaskHeader>
           <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <TaskCategory>{displayData.policyOwner || displayData.insuredName}</TaskCategory>
            <TaskDate style={{ color: severity.color }}>
              {timeRemainingText}
            </TaskDate>
          </div>
           <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <TaskId>{displayData.caseId}</TaskId>
              {displayData?.claimedAmount && <ClaimedAmount>IDR {displayData.claimedAmount}</ClaimedAmount>}
            </div>
        </TaskHeader>
        {/* <TaskDescription>
          <CheckIcon style={{ color: severity.color }}>✓</CheckIcon>
          {displayData.statusLabel || severity.label}
        </TaskDescription> */}
      </TaskCard>
    );
  };

  const handleResizeStart = () => {
    setIsResizing(true);
  };

  return (
    <Container data-container="workbench">
      <MainContent>
        <TopSection>
          <WelcomeCard>
            <SLACircle percentage={stats.slaPercentage}>
              <SLAInner>
                <SLALabelHeading>SLA</SLALabelHeading>
                <SLAValue>{stats.slaPercentage}%</SLAValue>
                <SLALabel>On Track</SLALabel>
              </SLAInner>
            </SLACircle>
            <WelcomeContent>
              <WelcomeTextSecion>
              <WelcomeTitle>Welcome, {getUserData()?.firstname || 'User'} !</WelcomeTitle>
              <AlertBadge>
                <AlertCount>{stats.nearSLA}</AlertCount>
                Claims nearing SLA in 24 hrs!
              </AlertBadge>
              </WelcomeTextSecion>
              <CreateClaimButton onClick={handleCreateClaim}>
                <Plus size={24} />
                Create Claim
                </CreateClaimButton>
            </WelcomeContent>
          </WelcomeCard>

          <StatsGrid>
            <StatCard>
              <StatHeader>
                <div>
                  <StatValue>{stats.totalCases}</StatValue>
                  <StatLabel>Your Cases</StatLabel>
                </div>
                <StatIcon><FiTrendingUp /></StatIcon>
              </StatHeader>
            </StatCard>
            <StatCard>
              <StatHeader>
                <div>
                  <StatValue>
                    {stats.avgTAT && !isNaN(stats.avgTAT) 
                      ? (stats.avgTAT < 0.01 ? '<0.1 sec' : Number(stats.avgTAT).toFixed(2) + ' hrs')
                      : '0.00 secs'
                    } <span style={{ fontSize: '1rem' }}></span>
                  </StatValue>
                  <StatLabel>Overall TAT</StatLabel>
                </div>
                <StatIcon><FiClock /></StatIcon>
              </StatHeader>
            </StatCard>
            <StatCard>
              <StatHeader>
                <div>
                  <StatValue>{stats.nearSLA}</StatValue>
                  <StatLabel>Near SLA (24 hrs)</StatLabel>
                </div>
                <StatIcon><FiAlertCircle /></StatIcon>
              </StatHeader>
            </StatCard>
            <StatCard>
              <StatHeader>
                <div>
                  <StatValue>{stats.highRisk}</StatValue>
                  <StatLabel>High Risk</StatLabel>
                </div>
                <StatIcon><FiBarChart2 /></StatIcon>
              </StatHeader>
            </StatCard>
          </StatsGrid>
        </TopSection>

        <SectionHeader>
          <SectionTitle>Your Priority Queue for Today</SectionTitle>
          <ViewControls>
            <ViewButton>Select View</ViewButton>
            <ViewButton><FiList /></ViewButton>
            <ViewButton><FiGrid /></ViewButton>
          </ViewControls>
        </SectionHeader>

        <KanbanContainer>
          <KanbanColumn>
            <ColumnHeader>
              <ColumnTitle>
                <StatusDot color="#ef4444" />
                Critical Tasks
              </ColumnTitle>
              <ColumnDescription>
                Requires immediate action to prevent SLA breach or escalation
              </ColumnDescription>
              {/* <SortControls>
                <SortButton active={activeSort.critical === 'SLA Due'} onClick={() => setActiveSort({...activeSort, critical: 'SLA Due'})}>
                  SLA Due
                </SortButton>
                <SortButton active={activeSort.critical === 'High Priority'} onClick={() => setActiveSort({...activeSort, critical: 'High Priority'})}>
                  High Priority
                </SortButton>
                <SortButton>+1</SortButton>
              </SortControls> */}
            </ColumnHeader>
            <TaskList>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>{error}</div>
              ) : tasks.critical.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No critical tasks</div>
              ) : (
                <>
                  {tasks.critical.map(task => renderTask(task))}
                  {pagination.critical.hasMore && (
                    <div ref={criticalLoaderRef} style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                      {pagination.critical.loading ? 'Loading more...' : ''}
                    </div>
                  )}
                </>
              )}
            </TaskList>
          </KanbanColumn>

          <KanbanColumn>
            <ColumnHeader>
              <ColumnTitle>
                <StatusDot color="#f59e0b" />
                Need Attention
              </ColumnTitle>
              <ColumnDescription>
                Review required — may impact decision or delay processing
              </ColumnDescription>
              {/* <SortControls>
                <SortButton active={activeSort.attention === 'SLA Due'} onClick={() => setActiveSort({...activeSort, attention: 'SLA Due'})}>
                  SLA Due
                </SortButton>
                <SortButton active={activeSort.attention === 'High Priority'} onClick={() => setActiveSort({...activeSort, attention: 'High Priority'})}>
                  High Priority
                </SortButton>
              </SortControls> */}
            </ColumnHeader>
            <TaskList>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>{error}</div>
              ) : tasks.attention.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No tasks need attention</div>
              ) : (
                <>
                  {tasks.attention.map(task => renderTask(task))}
                  {pagination.attention.hasMore && (
                    <div ref={attentionLoaderRef} style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                      {pagination.attention.loading ? 'Loading more...' : ''}
                    </div>
                  )}
                </>
              )}
            </TaskList>
          </KanbanColumn>

          <KanbanColumn>
            <ColumnHeader>
              <ColumnTitle>
                <StatusDot color="#3b82f6" />
                Pending Tasks
              </ColumnTitle>
              <ColumnDescription>
                Actionable after critical and attention items are cleared
              </ColumnDescription>
              {/* <SortControls>
                <SortButton active={activeSort.pending === 'Quick to Close'} onClick={() => setActiveSort({...activeSort, pending: 'Quick to Close'})}>
                  Quick to Close
                </SortButton>
                <SortButton active={activeSort.pending === 'High Value'} onClick={() => setActiveSort({...activeSort, pending: 'High Value'})}>
                  High Value
                </SortButton>
              </SortControls> */}
            </ColumnHeader>
            <TaskList>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>{error}</div>
              ) : tasks.pending.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No pending tasks</div>
              ) : (
                <>
                  {tasks.pending.map(task => renderTask(task))}
                  {pagination.pending.hasMore && (
                    <div ref={pendingLoaderRef} style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                      {pagination.pending.loading ? 'Loading more...' : ''}
                    </div>
                  )}
                </>
              )}
            </TaskList>
          </KanbanColumn>
        </KanbanContainer>
      </MainContent>

      <ResizeHandle onMouseDown={handleResizeStart} />

      <Sidebar width={sidebarWidth} isOpen={isAISidebarOpen}>
        <MetaClaimsAI
          userName={getUserData()?.firstname || 'User'}
          userEmail={getUserData()?.username || ''}
          initialHiddenQuery="Provide a detailed executive summary of my current workbench tasks.\nUse well-structured Markdown and include:\n\n- A short overview paragraph (what my workload looks like right now).\n\n- Key risks or SLA concerns (if any).\n- Notable high-value or high-risk tasks I should prioritize."
          chatContext="listing"
          contextMessage="Ask me to fetch data, explain rules, or help you start working on a claim."
          showProgress={true}
          progressValue="55%"
          progressLabel="Progress"
          suggestions={[
            "How is my workload compared to yesterday?",
            "Which claims have the highest financial impact?",
            "Show me high-risk cases in my queue.",
            "What is causing delays in my cases?"
          ]}
          onClose={() => setIsAISidebarOpen(false)}
        />
      </Sidebar>

      <FloatingActionButton 
        show={!isAISidebarOpen} 
        onClick={() => setIsAISidebarOpen(true)}
        title="Open MetaClaims AI"
      >
        <img src="/images/AI_Bubble.png" alt="AI"  />
      </FloatingActionButton>
    </Container>
  );
};

export default WorkbenchListing;
